import React from "react";

import { Box, Divider, Theme, Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";

import { graphql } from "@/gql";
import { useFragment } from "@/hooks/useFragment";
import { colors } from "@/theme/overrides/colors";

import Duration from "./Duration";
import Macronutrients from "./Macronutrients";
import OptionMenu from "./OptionMenu";
import RecommendedCalories from "./RecommendedCalories";

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    backgroundColor: colors.white,
    padding: theme.spacing(2, 5),
    flexBasis: "100%",
    minHeight: 50,
  },
  divider: {
    height: 50,
    margin: theme.spacing(0, 2),
    color: colors.light,
    width: 1.5,
  },
}));

type TemplateItemProps = {
  id: string;
  onDelete: (templateId: string) => Promise<void>;
  onDuplicate: (templateId: string) => Promise<void>;
};

const templateItemFragment = graphql(`
  fragment TemplateItem on MealPlanTemplate {
    id
    coachId
    name
    durationDays
    recommendedCalories
    ...TemplateItemMacronutrients
  }
`);

const TemplateItem: React.FC<TemplateItemProps> = ({
  id,
  onDelete,
  onDuplicate,
}) => {
  const { classes } = useStyles();

  const [template] = useFragment(
    templateItemFragment,
    `MealPlanTemplate:${id}`
  );

  if (template == null) return null;

  const handleDelete = () => onDelete(template?.id ?? "");
  const handleDuplicate = () => onDuplicate(template.id ?? "");

  return (
    <Box
      className={classes.root}
      display="flex"
      justifyContent="space-between"
      alignContent="stretch"
      flexWrap="wrap"
    >
      <Box display="flex" flexDirection="column" flexGrow="3">
        <Typography variant="subtitle1" color="textPrimary">
          {template.name}
        </Typography>
        <Macronutrients data={template} />
      </Box>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-around"
        flexGrow="1"
      >
        <RecommendedCalories
          recommendedCalories={template.recommendedCalories}
        />
        <Divider className={classes.divider} />
        <Duration duration={template.durationDays} />
        <OptionMenu
          templateId={template.id}
          ownerId={template.coachId ?? ""}
          onDelete={handleDelete}
          onDuplicate={handleDuplicate}
        />
      </Box>
    </Box>
  );
};

export default TemplateItem;
